/**
 * The brain list's own filters on an AI call campaign: the brain's verdict,
 * whether anybody worked the company, a follow-up that came due, whether the
 * company heard from us, and whether it opened its report.
 *
 * Each "any" is an absent key, never a stored "any": the server's schema is
 * strict and an absent filter is the one that does not narrow.
 */
import { useT } from "../../../../i18n/I18nProvider";
import {
  BRAIN_WORKED_VALUES,
  FIT_DECISIONS,
  type BrainAudienceFilter,
  type BrainWorked,
  type FitDecision,
} from "../../../../lib/aiCampaigns";
import { CheckField, SelectInput } from "../../../ui";
import { Field } from "../../marketing/shared";
import { ToggleRow } from "../../settings/panel/fields";

const ANY = "any";

/** A yes / no / any answer as the select's value. */
function triValue(value: boolean | undefined): string {
  return value === undefined ? ANY : value ? "yes" : "no";
}

function fromTri(value: string): boolean | undefined {
  return value === ANY ? undefined : value === "yes";
}

export function BrainAudienceFields({
  filter,
  onChange,
  disabled,
}: {
  filter: BrainAudienceFilter;
  onChange: (next: BrainAudienceFilter) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const fits = filter.fit ?? [];

  function set<K extends keyof BrainAudienceFilter>(key: K, value: BrainAudienceFilter[K]) {
    const next = { ...filter, [key]: value };
    if (value === undefined) delete next[key];
    onChange(next);
  }

  function toggleFit(fit: FitDecision, on: boolean) {
    const nextFits = on ? [...new Set([...fits, fit])] : fits.filter((f) => f !== fit);
    set("fit", nextFits.length > 0 ? FIT_DECISIONS.filter((f) => nextFits.includes(f)) : undefined);
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label={t("aiCalls.campaigns.brain.fit")} hint={t("aiCalls.campaigns.brain.fitHint")} className="sm:col-span-2">
        <div className="flex flex-wrap gap-2">
          {FIT_DECISIONS.map((fit) => (
            <CheckField
              key={fit}
              checked={fits.includes(fit)}
              onChange={(on) => toggleFit(fit, on)}
              label={t(`aiCalls.campaigns.brain.fits.${fit}`)}
              disabled={disabled}
            />
          ))}
        </div>
      </Field>

      <Field label={t("aiCalls.campaigns.brain.worked")}>
        <SelectInput
          value={filter.worked ?? ANY}
          onChange={(v) => set("worked", v === ANY ? undefined : (v as BrainWorked))}
          label={t("aiCalls.campaigns.brain.worked")}
          disabled={disabled}
        >
          <option value={ANY}>{t("aiCalls.campaigns.any")}</option>
          {BRAIN_WORKED_VALUES.map((w) => (
            <option key={w} value={w}>
              {t(`aiCalls.campaigns.brain.workedValues.${w}`)}
            </option>
          ))}
        </SelectInput>
      </Field>

      <Field label={t("aiCalls.campaigns.brain.heardFromUs")} hint={t("aiCalls.campaigns.brain.heardFromUsHint")}>
        <SelectInput
          value={triValue(filter.heardFromUs)}
          onChange={(v) => set("heardFromUs", fromTri(v))}
          label={t("aiCalls.campaigns.brain.heardFromUs")}
          disabled={disabled}
        >
          <option value={ANY}>{t("aiCalls.campaigns.any")}</option>
          <option value="yes">{t("aiCalls.campaigns.yes")}</option>
          <option value="no">{t("aiCalls.campaigns.no")}</option>
        </SelectInput>
      </Field>

      <Field label={t("aiCalls.campaigns.brain.reportOpened")}>
        <SelectInput
          value={triValue(filter.reportOpened)}
          onChange={(v) => set("reportOpened", fromTri(v))}
          label={t("aiCalls.campaigns.brain.reportOpened")}
          disabled={disabled}
        >
          <option value={ANY}>{t("aiCalls.campaigns.any")}</option>
          <option value="yes">{t("aiCalls.campaigns.yes")}</option>
          <option value="no">{t("aiCalls.campaigns.no")}</option>
        </SelectInput>
      </Field>

      <div className="flex items-end">
        <ToggleRow
          label={t("aiCalls.campaigns.brain.followUpDue")}
          hint={t("aiCalls.campaigns.brain.followUpDueHint")}
          checked={filter.followUpDue === true}
          disabled={disabled}
          onChange={(on) => set("followUpDue", on ? true : undefined)}
        />
      </div>
    </div>
  );
}
